import { useEffect, useMemo, useState } from 'react';
import './AiProviderManual.css';
import {
  testManualConnection,
  connectManualProvider,
  type ManualVendor,
  type AiProviderHealthResult,
} from '../../../api/aiProviders';
import { ProviderIcon } from './AiProviderIcons';

/**
 * 외부 API LLM 수동 연결 모달.
 * 종류 선택(프리셋) → API Key/Base URL/Model 입력 → 테스트 → 저장.
 * 저장은 ~/.claude/.env 에 env 키 머지 작성(자동 감지 반영) — admin BFF → backend 직접.
 *
 * 주의: OpenAI 계열(OpenAI/Codex/Gemini/Local/호환) 은 동일 OPENAI_* env 키를 공유하므로
 *       동시에는 1개만 유지된다(새로 저장 시 교체). Anthropic·Claude-gateway 는 별도 슬롯.
 */

interface PresetDef {
  vendor: ManualVendor;
  label: string;
  iconVendor: string;
  baseLabel: string;
  basePlaceholder: string;
  baseDefault: string;
  baseRequired: boolean;
  keyRequired: boolean;
  modelPlaceholder: string;
  note?: string;
  // 입력값(api_key/base_url/model)을 env 키 맵으로 변환.
  buildKeys: (v: { key: string; base: string; model: string }) => Record<string, string>;
}

const PRESETS: PresetDef[] = [
  {
    vendor: 'anthropic', label: 'Claude API', iconVendor: 'Anthropic',
    baseLabel: 'Base URL (선택)', basePlaceholder: 'https://api.anthropic.com',
    baseDefault: '', baseRequired: false, keyRequired: true,
    modelPlaceholder: 'claude-opus-4-8 (선택)',
    buildKeys: ({ key, base, model }) => ({
      ANTHROPIC_API_KEY: key,
      ...(base ? { ANTHROPIC_BASE_URL: base } : {}),
      ...(model ? { ANTHROPIC_MODEL: model } : {}),
    }),
  },
  {
    vendor: 'openai', label: 'OpenAI / Codex API', iconVendor: 'OpenAI',
    baseLabel: 'Base URL (선택)', basePlaceholder: 'https://api.openai.com/v1',
    baseDefault: '', baseRequired: false, keyRequired: true,
    modelPlaceholder: 'gpt-4o (선택)',
    buildKeys: ({ key, base, model }) => ({
      OPENAI_API_KEY: key,
      ...(base ? { OPENAI_BASE_URL: base } : {}),
      ...(model ? { OPENAI_MODEL: model } : {}),
    }),
  },
  {
    vendor: 'gemini', label: 'Gemini API', iconVendor: 'Google',
    baseLabel: 'Base URL', basePlaceholder: 'https://generativelanguage.googleapis.com/v1beta/openai',
    baseDefault: 'https://generativelanguage.googleapis.com/v1beta/openai',
    baseRequired: true, keyRequired: true,
    modelPlaceholder: 'gemini-2.0-flash (선택)',
    note: 'Gemini 는 OpenAI 호환 엔드포인트로 연결됩니다. OpenAI 계열 슬롯을 공유합니다.',
    buildKeys: ({ key, base, model }) => ({
      OPENAI_API_KEY: key,
      OPENAI_BASE_URL: base || 'https://generativelanguage.googleapis.com/v1beta/openai',
      ...(model ? { OPENAI_MODEL: model } : {}),
    }),
  },
  {
    vendor: 'local', label: 'Local LLM', iconVendor: 'OpenAI',
    baseLabel: 'Base URL', basePlaceholder: 'http://localhost:11434/v1 (Ollama/LM Studio)',
    baseDefault: 'http://localhost:11434/v1', baseRequired: true, keyRequired: false,
    modelPlaceholder: 'llama3.2 (선택)',
    note: '로컬 OpenAI 호환 서버(Ollama, LM Studio 등). API Key 가 없으면 자리표시자로 저장됩니다.',
    buildKeys: ({ key, base, model }) => ({
      OPENAI_API_KEY: key || 'local',
      OPENAI_BASE_URL: base || 'http://localhost:11434/v1',
      ...(model ? { OPENAI_MODEL: model } : {}),
    }),
  },
  {
    vendor: 'openai-compatible', label: 'OpenAI 호환', iconVendor: 'OpenAI',
    baseLabel: 'Base URL', basePlaceholder: 'https://your-endpoint/v1',
    baseDefault: '', baseRequired: true, keyRequired: true,
    modelPlaceholder: '모델명 (선택)',
    note: 'OpenAI 호환 API(/v1/models 지원). OpenAI 계열 슬롯을 공유합니다.',
    buildKeys: ({ key, base, model }) => ({
      OPENAI_API_KEY: key,
      OPENAI_BASE_URL: base,
      ...(model ? { OPENAI_MODEL: model } : {}),
    }),
  },
  {
    vendor: 'claude-gateway', label: 'Claude Gateway', iconVendor: 'Anthropic',
    baseLabel: 'Gateway URL', basePlaceholder: 'http://localhost:8080',
    baseDefault: '', baseRequired: true, keyRequired: true,
    modelPlaceholder: '',
    note: '로컬 Claude Code Gateway. CLAUDE_API_URL/CLAUDE_API_KEY 로 저장됩니다.',
    buildKeys: ({ key, base }) => ({ CLAUDE_API_KEY: key, CLAUDE_API_URL: base }),
  },
];

const IconX = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
export function ManualConnectModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [vendor, setVendor] = useState<ManualVendor>('anthropic');
  const preset = useMemo(() => PRESETS.find((p) => p.vendor === vendor)!, [vendor]);
  const [key, setKey] = useState('');
  const [base, setBase] = useState('');
  const [model, setModel] = useState('');
  const [test, setTest] = useState<AiProviderHealthResult | 'loading' | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  // 프리셋 변경 시 입력 초기화(base 는 기본값으로).
  function pickPreset(v: ManualVendor) {
    const p = PRESETS.find((x) => x.vendor === v)!;
    setVendor(v);
    setBase(p.baseDefault);
    setTest(null);
    setErr(null);
  }

  const canSubmit =
    (!preset.keyRequired || key.trim().length > 0) &&
    (!preset.baseRequired || base.trim().length > 0);

  async function runTest() {
    setTest('loading');
    setErr(null);
    try {
      setTest(await testManualConnection({ vendor, base_url: base.trim() || undefined, api_key: key.trim() || undefined }));
    } catch (e) {
      setTest({ ok: false, latency_ms: 0, detail: e instanceof Error ? e.message : String(e) });
    }
  }

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      const keys = preset.buildKeys({ key: key.trim(), base: base.trim(), model: model.trim() });
      const r = await connectManualProvider(keys);
      if (r.ok) { onSaved(); onClose(); }
      else setErr(r.error ?? '저장 실패');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="aipm-backdrop" role="presentation" onClick={onClose}>
      <div className="aipm-modal" role="dialog" aria-modal="true" aria-label="외부 API LLM 연결" onClick={(e) => e.stopPropagation()}>
        <div className="aipm-head">
          <h3>외부 API LLM 연결</h3>
          <button type="button" className="aipm-close" onClick={onClose} aria-label="닫기"><IconX /></button>
        </div>

        <div className="aipm-body">
          <div className="aipm-presets" role="tablist" aria-label="연결 종류">
            {PRESETS.map((p) => (
              <button
                key={p.vendor}
                type="button"
                role="tab"
                aria-selected={vendor === p.vendor}
                className={`aipm-preset${vendor === p.vendor ? ' is-active' : ''}`}
                onClick={() => pickPreset(p.vendor)}
              >
                <span className="aipm-preset__icon"><ProviderIcon vendor={p.iconVendor} size={18} /></span>
                {p.label}
              </button>
            ))}
          </div>

          {preset.note && <p className="aipm-note">{preset.note}</p>}

          <div className="aipm-field">
            <label>API Key{preset.keyRequired ? ' *' : ' (선택)'}</label>
            <input
              type="password"
              value={key}
              autoComplete="off"
              placeholder={preset.keyRequired ? 'sk-... / API 키' : '비워두면 자리표시자 사용'}
              onChange={(e) => { setKey(e.target.value); setTest(null); }}
            />
          </div>

          <div className="aipm-field">
            <label>{preset.baseLabel}{preset.baseRequired ? ' *' : ''}</label>
            <input
              type="text"
              value={base}
              placeholder={preset.basePlaceholder}
              onChange={(e) => { setBase(e.target.value); setTest(null); }}
            />
          </div>

          {preset.modelPlaceholder && (
            <div className="aipm-field">
              <label>Model (선택)</label>
              <input type="text" value={model} placeholder={preset.modelPlaceholder} onChange={(e) => setModel(e.target.value)} />
              <span className="aipm-field__hint">비워두면 provider 기본 모델을 사용합니다.</span>
            </div>
          )}

          {test && test !== 'loading' && (
            <div className={`aipm-test ${test.ok ? 'aipm-test--ok' : 'aipm-test--bad'}`} role="status">
              <span className="aipm-test__dot" />
              {test.ok
                ? `연결 정상${test.latency_ms ? ` · ${test.latency_ms}ms` : ''}`
                : `연결 실패: ${test.detail ?? ''}`}
            </div>
          )}
          {test === 'loading' && (
            <div className="aipm-test aipm-test--ok" role="status">
              <span className="aipm-test__dot aipm-test__dot--loading" /> 테스트 중…
            </div>
          )}
          {err && <div className="aipm-test aipm-test--bad" role="alert"><span className="aipm-test__dot" />{err}</div>}
        </div>

        <div className="aipm-foot">
          <button type="button" className="aipm-btn" onClick={() => void runTest()} disabled={!canSubmit || test === 'loading'}>
            {test === 'loading' ? '테스트 중…' : '연결 테스트'}
          </button>
          <span className="aipm-foot__spacer" />
          <button type="button" className="aipm-btn" onClick={onClose}>취소</button>
          <button type="button" className="aipm-btn aipm-btn--primary" onClick={() => void save()} disabled={!canSubmit || saving}>
            {saving ? '저장 중…' : '연결 저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
